import { useEffect, useState } from "react";
import { X, FileSignature, Inbox, Plus, FileDown, CheckCircle2, Trash2, Loader2, Send } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import api, { formatApiError, API } from "@/lib/api";

const REQ_STYLE = {
  "oczekuje": "bg-blue-500/15 text-blue-300 border-blue-500/50",
  "aneks_wysłany": "bg-amber-500/15 text-amber-300 border-amber-500/50",
  "zrealizowany": "bg-green-500/15 text-green-300 border-green-500/50",
  "odrzucony": "bg-slate-600/40 text-slate-400 border-slate-600",
};
const ANN_STYLE = {
  "wyslany": "bg-blue-500/15 text-blue-300 border-blue-500/50",
  "wysłany": "bg-blue-500/15 text-blue-300 border-blue-500/50",
  "zaakceptowany": "bg-amber-500/15 text-amber-300 border-amber-500/50",
  "wdrożony": "bg-green-500/15 text-green-300 border-green-500/50",
};

export default function RequestsPanel({ role, onClose }) {
  const { t } = useI18n();
  const canEdit = role !== "odczyt";
  const [tab, setTab] = useState("requests");
  const [reqs, setReqs] = useState([]);
  const [annexes, setAnnexes] = useState([]);
  const [plans, setPlans] = useState([]);
  const [clients, setClients] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null); // { client_id, to_plan, to_download_mbps, to_upload_mbps, note, service_request_id }

  const load = async () => {
    try {
      const [r, a] = await Promise.all([api.get("/service-requests"), api.get("/annexes")]);
      setReqs(r.data || []); setAnnexes(a.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => {
    load();
    api.get("/settings").then((r) => setPlans(r.data?.speed_plans || [])).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {});
  }, []);

  const dlPdf = async (aid) => {
    try {
      const r = await api.get(`/annexes/${aid}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const w = window.open(url, "_blank"); if (!w) { const el = document.createElement("a"); el.href = url; el.download = "aneks.pdf"; el.click(); }
    } catch { toast.error("Nie udało się pobrać PDF"); }
  };

  const startAnnexFrom = (rq) => {
    setForm({ client_id: rq.client_id, to_plan: rq.requested_plan || "", to_download_mbps: "", to_upload_mbps: "",
      note: rq.note ? `Na podstawie wniosku klienta: ${rq.note}` : "", service_request_id: rq.id });
    setTab("annexes");
  };
  const startAnnexManual = () => {
    setForm({ client_id: "", to_plan: "", to_download_mbps: "", to_upload_mbps: "", note: "", service_request_id: null });
    setTab("annexes");
  };

  const submitAnnex = async () => {
    if (!form.client_id || !form.to_plan) { toast.error("Wybierz klienta i nowy plan"); return; }
    setBusy(true);
    try {
      await api.post("/annexes", {
        client_id: form.client_id, to_plan: form.to_plan,
        to_download_mbps: form.to_download_mbps === "" ? null : Number(form.to_download_mbps),
        to_upload_mbps: form.to_upload_mbps === "" ? null : Number(form.to_upload_mbps),
        note: form.note, service_request_id: form.service_request_id,
      });
      toast.success("Aneks wysłany do klienta"); setForm(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const applyAnnex = async (a) => {
    if (!window.confirm(`Wdrożyć aneks ${a.annex_no}? Parametry usługi klienta zostaną automatycznie zaktualizowane.`)) return;
    try { await api.post(`/annexes/${a.id}/apply`); toast.success("Wdrożono — parametry klienta zaktualizowane"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delAnnex = async (a) => {
    if (!window.confirm(`Usunąć aneks ${a.annex_no}?`)) return;
    try { await api.delete(`/annexes/${a.id}`); toast.success("Usunięto"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const rejectReq = async (rq) => {
    try { await api.post(`/service-requests/${rq.id}/status`, { status: "odrzucony" }); toast.success("Wniosek odrzucony"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="absolute inset-0 bg-slate-900 flex flex-col" data-testid="requests-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
        <div className="flex items-center gap-2 font-heading font-bold text-white"><FileSignature size={18} className="text-cyan-400" /> {t("Wnioski / Aneksy")}</div>
        <button onClick={onClose} data-testid="requests-close" className="text-slate-400 hover:text-white"><X size={20} /></button>
      </div>

      <div className="flex border-b border-slate-800">
        <button onClick={() => setTab("requests")} data-testid="requests-tab-reqs" className={`flex-1 py-2.5 text-sm flex items-center justify-center gap-2 ${tab === "requests" ? "text-cyan-300 border-b-2 border-cyan-400" : "text-slate-400"}`}><Inbox size={15} /> Wnioski klientów ({reqs.filter((r) => r.status === "oczekuje").length})</button>
        <button onClick={() => setTab("annexes")} data-testid="requests-tab-annexes" className={`flex-1 py-2.5 text-sm flex items-center justify-center gap-2 ${tab === "annexes" ? "text-cyan-300 border-b-2 border-cyan-400" : "text-slate-400"}`}><FileSignature size={15} /> Aneksy ({annexes.length})</button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {tab === "requests" && (
          reqs.length === 0 ? <div className="text-sm text-slate-500 text-center py-10">{t("Brak wniosków od klientów.")}</div> :
          reqs.map((rq) => (
            <div key={rq.id} className="rounded-lg border border-slate-700 bg-slate-800/40 p-3" data-testid={`req-${rq.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-medium text-white text-sm">{rq.name}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{rq.current_plan || "-"} → <span className="text-cyan-300">{rq.requested_plan}</span></div>
                  {rq.note && <div className="text-xs text-slate-500 mt-1">{rq.note}</div>}
                  <div className="text-[10px] text-slate-600 mt-1">{(rq.at || "").slice(0, 16).replace("T", " ")}</div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded border ${REQ_STYLE[rq.status] || REQ_STYLE.oczekuje}`}>{rq.status}</span>
              </div>
              {canEdit && rq.status === "oczekuje" && (
                <div className="flex gap-2 mt-2">
                  <button onClick={() => startAnnexFrom(rq)} data-testid={`req-annex-${rq.id}`} className="text-xs bg-cyan-600 hover:bg-cyan-500 text-white rounded px-3 py-1.5 flex items-center gap-1"><FileSignature size={13} /> {t("Generuj Aneks")}</button>
                  <button onClick={() => rejectReq(rq)} data-testid={`req-reject-${rq.id}`} className="text-xs bg-slate-700 hover:bg-slate-600 text-slate-300 rounded px-3 py-1.5">{t("Odrzuć")}</button>
                </div>
              )}
            </div>
          ))
        )}

        {tab === "annexes" && (
          <>
            {canEdit && !form && (
              <button onClick={startAnnexManual} data-testid="annex-new" className="w-full text-sm bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg py-2.5 flex items-center justify-center gap-2"><Plus size={15} /> {t("Nowy aneks (ręcznie)")}</button>
            )}

            {form && (
              <div className="rounded-lg border border-cyan-700/50 bg-slate-800/60 p-3 space-y-2" data-testid="annex-form">
                <div className="text-sm font-medium text-white">Nowy aneks {form.service_request_id ? "(z wniosku klienta)" : ""}</div>
                <select value={form.client_id} disabled={!!form.service_request_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="annex-client" className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-2 text-sm text-white disabled:opacity-60">
                  <option value="">{t("— wybierz klienta —")}</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name} {c.speed_plan ? `(${c.speed_plan})` : ""}</option>)}
                </select>
                <select value={form.to_plan} onChange={(e) => setForm({ ...form, to_plan: e.target.value })} data-testid="annex-plan" className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-2 text-sm text-white">
                  <option value="">{t("— nowy plan —")}</option>
                  {plans.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <div className="grid grid-cols-2 gap-2">
                  <input type="number" placeholder="Download [Mb/s]" value={form.to_download_mbps} onChange={(e) => setForm({ ...form, to_download_mbps: e.target.value })} data-testid="annex-down" className="bg-slate-950 border border-slate-700 rounded px-2 py-2 text-sm text-white" />
                  <input type="number" placeholder="Upload [Mb/s]" value={form.to_upload_mbps} onChange={(e) => setForm({ ...form, to_upload_mbps: e.target.value })} data-testid="annex-up" className="bg-slate-950 border border-slate-700 rounded px-2 py-2 text-sm text-white" />
                </div>
                <textarea placeholder={t("Uwagi / dodatkowe postanowienia")} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} data-testid="annex-note" className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-2 text-sm text-white" />
                <div className="flex gap-2">
                  <button onClick={submitAnnex} disabled={busy} data-testid="annex-submit" className="flex-1 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white rounded py-2 text-sm flex items-center justify-center gap-2">{busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Wyślij do klienta</button>
                  <button onClick={() => setForm(null)} className="bg-slate-700 hover:bg-slate-600 text-slate-300 rounded px-4 text-sm">{t("Anuluj")}</button>
                </div>
              </div>
            )}

            {annexes.length === 0 && !form ? <div className="text-sm text-slate-500 text-center py-8">{t("Brak aneksów.")}</div> :
              annexes.map((a) => (
                <div key={a.id} className="rounded-lg border border-slate-700 bg-slate-800/40 p-3" data-testid={`annex-${a.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-medium text-white text-sm font-mono">{a.annex_no}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{a.client_name} · {a.from_plan || "-"} → <span className="text-cyan-300">{a.to_plan}</span> {(a.to_download_mbps || a.to_upload_mbps) ? `(${a.to_download_mbps || "?"}/${a.to_upload_mbps || "?"} Mb/s)` : ""}</div>
                      <div className="text-[10px] text-slate-600 mt-1">{(a.created_at || "").slice(0, 16).replace("T", " ")}</div>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded border ${ANN_STYLE[a.status] || ANN_STYLE.wyslany}`}>{a.status}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <button onClick={() => dlPdf(a.id)} data-testid={`annex-pdf-${a.id}`} className="text-xs bg-slate-700 hover:bg-slate-600 text-slate-200 rounded px-3 py-1.5 flex items-center gap-1"><FileDown size={13} /> PDF</button>
                    {canEdit && a.status === "zaakceptowany" && (
                      <button onClick={() => applyAnnex(a)} data-testid={`annex-apply-${a.id}`} className="text-xs bg-green-600 hover:bg-green-500 text-white rounded px-3 py-1.5 flex items-center gap-1"><CheckCircle2 size={13} /> {t("Wdróż (zastosuj zmiany)")}</button>
                    )}
                    {role === "admin" && a.status !== "wdrożony" && (
                      <button onClick={() => delAnnex(a)} data-testid={`annex-del-${a.id}`} className="text-xs bg-slate-800 hover:bg-red-900/40 text-red-300 rounded px-3 py-1.5 flex items-center gap-1"><Trash2 size={13} /></button>
                    )}
                  </div>
                </div>
              ))}
          </>
        )}
      </div>
    </div>
  );
}
